import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { uploadFile } from "@/lib/chatUpload";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Paperclip, Loader2 } from "lucide-react";

const money = (v) => `${Number(v || 0).toFixed(2)} ر.س`;
const fmt = (s) => (s ? new Date(s).toLocaleDateString("ar-SA-u-nu-latn", { dateStyle: "medium" }) : "—");
export const RECEIPT_ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";

export const openReceipt = async (fileId) => {
  const w = window.open("", "_blank");
  try {
    const res = await api.get(`/chat-files/file/${fileId}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    if (w) { w.location.href = url; } else { window.location.href = url; }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch { if (w) { w.close(); } toast.error("تعذر فتح الإيصال"); }
};

export const uploadReceipt = (file, onProgress = () => {}) => {
  if (!RECEIPT_ACCEPT.split(",").includes(file.type)) { throw new Error("المسموح صورة أو PDF فقط"); }
  if (file.size > 10 * 1024 * 1024) { throw new Error("حجم الملف أكبر من 10 ميجابايت"); }
  return uploadFile("/admin/payouts/receipt/init", file, onProgress).then((r) => r.attachment.file_id);
};

export function ReceiptButton({ fileId, testid }) {
  return (
    <Button size="sm" variant="outline" className="h-7 rounded-full gap-1 text-xs" onClick={() => openReceipt(fileId)} data-testid={testid}>
      <FileText className="w-3.5 h-3.5" /> عرض الإيصال
    </Button>
  );
}

function AttachReceipt({ transfer, onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) { return; }
    setBusy(true);
    try {
      const fid = await uploadReceipt(file);
      await api.post(`/admin/payouts/history/${transfer.id}/receipt`, { receipt_file_id: fid });
      toast.success("تم إرفاق الإيصال"); onDone();
    } catch (err) { toast.error(err.response ? formatApiError(err.response.data?.detail) : err.message); }
    setBusy(false);
  };
  return (
    <>
      <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" disabled={busy} onClick={() => ref.current?.click()} data-testid={`transfer-attach-${transfer.id}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />} {transfer.receipt_file_id ? "استبدال" : "إرفاق إيصال"}
      </Button>
      <input ref={ref} type="file" accept={RECEIPT_ACCEPT} hidden onChange={onFile} data-testid={`transfer-file-${transfer.id}`} />
    </>
  );
}

export function TransfersList({ transfers, admin = false, onChanged }) {
  if (!transfers?.length) { return <p className="text-sm text-muted-foreground" data-testid="transfers-empty">لا توجد تحويلات مسجلة بعد</p>; }
  return (
    <div className="divide-y divide-border border border-border rounded-xl" data-testid="transfers-list">
      {transfers.map((t) => (
        <div key={t.id} className="p-3 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`transfer-row-${t.id}`}>
          <div><div className="font-semibold">{fmt(t.transferred_at)}</div><div className="text-xs text-muted-foreground">{t.count} عملية{t.note && ` · ${t.note}`}</div></div>
          <div className="font-bold text-emerald-700">{money(t.amount)}</div>
          <div className="flex items-center gap-1">
            {t.receipt_file_id ? <ReceiptButton fileId={t.receipt_file_id} testid={`transfer-receipt-${t.id}`} /> : <span className="text-xs text-muted-foreground">بدون إيصال</span>}
            {admin && <AttachReceipt transfer={t} onDone={onChanged} />}
          </div>
        </div>
      ))}
    </div>
  );
}

export function TransfersDialog({ partner, onClose, onChanged }) {
  return (
    <Dialog open={!!partner} onOpenChange={(v) => !v && onClose()}>
      <DialogContent dir="rtl" className="max-w-xl" data-testid="transfers-dialog">
        <DialogHeader><DialogTitle>سجل التحويلات — {partner?.name}</DialogTitle></DialogHeader>
        <TransfersList transfers={partner?.transfers} admin onChanged={onChanged} />
      </DialogContent>
    </Dialog>
  );
}
